import React from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createGlobalStyle } from 'styled-components';

import { novaRoutes, ACQUISITION_ROOT, OPERATIONS_ROOT } from '@/nova/router';

/**
 * Two app-side fixes for the section label in the top breadcrumb:
 *   1. make it a client-side link to its area root, and
 *   2. pin its copy to the area's section name on Acquisition / Operations subpages.
 *
 * The breadcrumb (`.top-breadcrumb`) is config-driven layout chrome, reused across
 * every studio section. Its markup is: a home-icon `LinkContainer` → `/account/home`,
 * a dot, then a plain `Text` label (`.lbl`) whose copy is swapped per-section by a
 * config `propFunction` on the current path. The home icon navigates, but that label
 * is just text — and the path→label function can't be trusted on this area's detail
 * subpages (it has fallen back to "Stats" on a campaign detail). The detail routes now
 * live under their section prefix (e.g. `/account/marketing/campaign/:id`), but we
 * still normalise the label app-side so it is *guaranteed* to read "Acquisition" /
 * "Operations" regardless of what the remote propFunction resolves.
 *
 * The config lives on the remote templatization server (not in this repo), so we add
 * both behaviours from the app side — the same way the nova area already
 * reaches into this shared chrome (see `PageShell.styles.ts`). The fixed-route table
 * (`novaRoutes`) maps each path to its correct area root.
 *
 * Mounted once, globally, next to `RouteChangeHandler` in `routes.tsx`.
 */

/** `cursor: pointer` so the now-clickable section label reads as interactive. */
const BreadcrumbLabelCursor = createGlobalStyle`
  .top-breadcrumb .lbl {
    cursor: pointer;
  }
`;

/**
 * The section root for a path. The breadcrumb label names a *section* (Acquisition,
 * Operations, …) whose root URL is the area root the fixed-route table records via
 * each route's `areaRoot` (e.g. `/account/marketing/campaign/:id` → `/account/marketing`),
 * so match against it first; fall back to the `/account/<section>` prefix for
 * config-only sections that have no fixed route (their root *is* their prefix).
 */
const sectionRootFor = (pathname: string): string | null => {
  for (const route of novaRoutes) {
    if (matchPath(route.path, pathname)) return route.areaRoot;
  }

  if (pathname.startsWith('/account/')) {
    const section = pathname.split('/')[2];
    if (section) return `/account/${section}`;
  }

  return null;
};

/**
 * Display label per area root, for the two sections whose detail subpages we pin
 * (the config's path→label function has mislabelled them, e.g. a campaign detail as
 * "Stats"). Limited to these two so we never override a section whose config label is
 * already correct. Anything not listed keeps the config's own label.
 */
const SECTION_LABELS: Record<string, string> = {
  [ACQUISITION_ROOT]: 'Acquisition',
  [OPERATIONS_ROOT]: 'Operations',
};

const sectionLabelFor = (pathname: string): string | null => {
  const root = sectionRootFor(pathname);
  return root ? (SECTION_LABELS[root] ?? null) : null;
};

const StudioBreadcrumbNav = (): React.ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  // (1) Click the section label → navigate to its section root.
  React.useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      // Only the section label — not the home icon (its own LinkContainer) or the dot.
      if (!target || !target.closest('.top-breadcrumb .lbl')) return;

      const root = sectionRootFor(window.location.pathname);
      if (root && root !== window.location.pathname) {
        event.preventDefault();
        navigate(root);
      }
    };

    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [navigate]);

  // (2) Correct the section label's copy on cross-prefix subpages. The breadcrumb is
  // config-rendered chrome that re-asserts its own per-path label whenever the config
  // tree re-renders, so we observe the document and re-apply ours.
  React.useEffect(() => {
    const label = sectionLabelFor(pathname);
    if (!label) return; // No override for this section — leave the config label alone.
    const desired = t(label);

    const apply = (): void => {
      const el = document.querySelector('.top-breadcrumb .lbl');
      // Setting textContent re-enters this callback once, then matches and stops —
      // no loop, no fight with React unless the config genuinely re-renders the label.
      if (el && el.textContent !== desired) el.textContent = desired;
    };

    apply();
    // Observe the document (not just the breadcrumb element) so we re-apply whether
    // the config updates the label's TEXT or replaces the whole `.top-breadcrumb`
    // node — and so we also catch it mounting after this effect, without a poll.
    // MutationObserver coalesces records into a single microtask callback, so one
    // cheap querySelector per batch keeps this inexpensive even on live pages.
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [pathname, t]);

  return <BreadcrumbLabelCursor />;
};

export default StudioBreadcrumbNav;
